import { useEffect, useState } from 'react'

import type { Media, Reaction } from '../../api/types'
import { ChevronLeftIcon, ChevronRightIcon, ReplyIcon, XIcon } from '../../components/Icons'
import { Reactions } from './Reactions'

/**
 * Die Fotos eines Beitrags.
 *
 * Ein einzelnes Bild steht für sich und behält sein Seitenverhältnis -- ein
 * Hochformat vom Telefon in ein Quadrat zu schneiden nimmt gerade das weg,
 * worum es ging. Ab zwei Bildern wird daraus ein Raster aus Quadraten, denn
 * nebeneinander gestellte Bilder unterschiedlicher Form ergeben eine unruhige
 * Treppe.
 *
 * Ab dem fünften Bild zeigt die letzte Kachel, wie viele noch folgen. Die
 * Großansicht hat sie ohnehin alle.
 */
export function Gallery({
  media,
  reactions,
  canReact,
  onReact,
  onReply,
}: {
  media: Media[]
  reactions: Reaction[]
  canReact: boolean
  onReact: (emoji: string, mediaId: number) => void
  /**
   * Auf genau dieses Foto antworten.
   *
   * Der Knopf sitzt nur in der Großansicht und nicht auf der Kachel: die
   * öffnet die Großansicht, und ein zweites Ziel auf 56 Pixeln ist Gefummel.
   * Erst groß sieht man ohnehin, welches Bild man meint.
   */
  onReply?: (mediaId: number) => void
}) {
  const [open, setOpen] = useState<number | null>(null)

  if (media.length === 0) return null

  const fuer = (id: number) => reactions.filter((reaction) => reaction.media_id === id)

  const shown = media.length > 4 ? media.slice(0, 4) : media
  const hidden = media.length - shown.length

  return (
    <>
      <div
        className={media.length === 1 ? 'gallery gallery--single' : 'gallery'}
        style={media.length > 1 ? { gridTemplateColumns: `repeat(${media.length === 3 ? 3 : 2}, 1fr)` } : undefined}
      >
        {shown.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className="gallery__tile"
            onClick={() => setOpen(index)}
            aria-label={`Foto ${index + 1} von ${media.length} vergrößern`}
          >
            <img
              src={item.url}
              alt=""
              loading="lazy"
              // Die Maße stehen in der Antwort. Der Platz ist damit reserviert,
              // bevor das Bild da ist, und der Verlauf springt beim Laden nicht.
              width={item.width}
              height={item.height}
            />
            {hidden > 0 && index === shown.length - 1 && (
              <span className="gallery__more">+{hidden}</span>
            )}

            {/* Was an diesem Foto hängt, steht auf dem Foto -- sonst wüsste
                bei vier Bildern niemand, welches gemeint ist. */}
            {fuer(item.id).length > 0 && (
              <span className="gallery__reactions">
                {fuer(item.id).map((reaction) => (
                  <span key={reaction.emoji}>
                    {reaction.emoji}
                    {reaction.count > 1 && reaction.count}
                  </span>
                ))}
              </span>
            )}
          </button>
        ))}
      </div>

      {open !== null && (
        <Lightbox
          media={media}
          start={open}
          reactionsFor={fuer}
          canReact={canReact}
          onReact={onReact}
          onReply={onReply}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  )
}

/**
 * Großansicht.
 *
 * Liegt über allem, schließt mit Escape, Klick auf den Hintergrund oder
 * Wischen. Geblättert wird mit den Pfeiltasten -- am Telefon mit dem Daumen.
 */
function Lightbox({
  media,
  start,
  reactionsFor,
  canReact,
  onReact,
  onReply,
  onClose,
}: {
  media: Media[]
  start: number
  reactionsFor: (mediaId: number) => Reaction[]
  canReact: boolean
  onReact: (emoji: string, mediaId: number) => void
  onReply?: (mediaId: number) => void
  onClose: () => void
}) {
  const [index, setIndex] = useState(start)
  const [touchStart, setTouchStart] = useState<number | null>(null)

  const back = () => setIndex((i) => (i - 1 + media.length) % media.length)
  const forward = () => setIndex((i) => (i + 1) % media.length)

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowLeft') back()
      if (event.key === 'ArrowRight') forward()
    }
    window.addEventListener('keydown', onKey)

    // Der Verlauf darunter soll nicht mitscrollen, während oben ein Bild steht.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.length])

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Foto in Großansicht"
      onClick={onClose}
      onTouchStart={(event) => setTouchStart(event.touches[0]?.clientX ?? null)}
      onTouchEnd={(event) => {
        if (touchStart === null) return
        const delta = (event.changedTouches[0]?.clientX ?? touchStart) - touchStart
        // Unter 50 Pixeln war es ein Tippen und kein Wischen.
        if (delta > 50) back()
        else if (delta < -50) forward()
        setTouchStart(null)
      }}
    >
      <button type="button" className="lightbox__close" onClick={onClose} aria-label="Schließen">
        <XIcon size={22} />
      </button>

      {media.length > 1 && (
        <>
          <button
            type="button"
            className="lightbox__step lightbox__step--back"
            onClick={(event) => {
              event.stopPropagation()
              back()
            }}
            aria-label="Vorheriges Foto"
          >
            <ChevronLeftIcon size={26} />
          </button>
          <button
            type="button"
            className="lightbox__step lightbox__step--forward"
            onClick={(event) => {
              event.stopPropagation()
              forward()
            }}
            aria-label="Nächstes Foto"
          >
            <ChevronRightIcon size={26} />
          </button>
        </>
      )}

      <img
        className="lightbox__image"
        src={media[index]?.url}
        alt=""
        onClick={(event) => event.stopPropagation()}
      />

      <div className="lightbox__bar" onClick={(event) => event.stopPropagation()}>
        {media[index] && (canReact || reactionsFor(media[index].id).length > 0) && (
          <Reactions
            compact
            reactions={reactionsFor(media[index].id)}
            onPick={(emoji) => canReact && onReact(emoji, media[index]!.id)}
          />
        )}
        {onReply && media[index] && (
          <button
            type="button"
            className="lightbox__antwort"
            onClick={() => {
              onReply(media[index]!.id)
              // Zumachen gehört dazu: geantwortet wird unten im Schreibfeld,
              // und das liegt hinter diesem Bild.
              onClose()
            }}
          >
            <ReplyIcon size={16} />
            Auf dieses Foto antworten
          </button>
        )}
        {media.length > 1 && (
          <span className="lightbox__count">
            {index + 1} von {media.length}
          </span>
        )}
      </div>
    </div>
  )
}
